import { QueryClient } from '@tanstack/react-query';

import { getUserErrorMessage } from './error-handler';

// Types for query filters
interface ListFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
}

// Create a client with default options
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time: 5 minutes
      staleTime: 5 * 60 * 1000,
      // Cache time: 10 minutes
      gcTime: 10 * 60 * 1000,
      // Retry failed requests 3 times
      retry: 3,
      // Retry delay doubles each time (exponential backoff)
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Refetch on window focus
      refetchOnWindowFocus: false,
      // Refetch on reconnect: true = only stale queries (default behavior).
      // 'always' caused visible grid reloads when socket.io briefly reconnected,
      // triggering browser 'online' events and refetching ALL queries.
      refetchOnReconnect: true,
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
      // Show error notifications
      onError: (error: unknown) => {
        const _message = getUserErrorMessage(error);
        // Production error handling would use proper logging service

        // The error handler will already handle logging and reporting
        // Toast notifications will be handled by the notification system
      },
    },
  },
});

// Query keys factory for type safety and consistency
export const queryKeys = {
  all: ['tms'] as const,
  auth: {
    all: ['tms', 'auth'] as const,
    user: () => [...queryKeys.auth.all, 'user'] as const,
    profile: () => [...queryKeys.auth.all, 'profile'] as const,
  },
  orders: {
    all: ['tms', 'orders'] as const,
    lists: () => [...queryKeys.orders.all, 'list'] as const,
    list: (filters?: ListFilters) => [...queryKeys.orders.lists(), filters] as const,
    details: () => [...queryKeys.orders.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.orders.details(), id] as const,
  },
  shipments: {
    all: ['tms', 'shipments'] as const,
    lists: () => [...queryKeys.shipments.all, 'list'] as const,
    list: (filters?: ListFilters) => [...queryKeys.shipments.lists(), filters] as const,
    details: () => [...queryKeys.shipments.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.shipments.details(), id] as const,
  },
  customers: {
    all: ['tms', 'customers'] as const,
    lists: () => [...queryKeys.customers.all, 'list'] as const,
    list: (filters?: ListFilters) => [...queryKeys.customers.lists(), filters] as const,
    details: () => [...queryKeys.customers.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.customers.details(), id] as const,
  },
  dashboard: {
    all: ['tms', 'dashboard'] as const,
    metrics: (countries?: string[]) => [...queryKeys.dashboard.all, 'metrics', countries] as const,
    kpis: (countries?: string[]) => [...queryKeys.dashboard.all, 'kpis', countries] as const,
    activities: (limit?: number) => [...queryKeys.dashboard.all, 'activities', limit] as const,
    overview: (countries?: string[]) =>
      [...queryKeys.dashboard.all, 'overview', countries] as const,
    country: (countryCode: string) => [...queryKeys.dashboard.all, 'country', countryCode] as const,
    realtime: () => [...queryKeys.dashboard.all, 'realtime'] as const,
    vehicles: () => [...queryKeys.dashboard.all, 'realtime', 'vehicles'] as const,
    tracking: (vehicleId: string) =>
      [...queryKeys.dashboard.all, 'realtime', 'tracking', vehicleId] as const,
  },
  // Widget-specific query keys for dashboard widgets with caching
  widget: {
    all: ['tms', 'widget'] as const,
    endpoint: (endpoint: string) => ['tms', 'widget', 'endpoint', endpoint] as const,
    category: (category: string) => ['tms', 'widget', 'category', category] as const,
    layout: (layoutId: string) => ['tms', 'widget', 'layout', layoutId] as const,
  },
  // Reporting query keys
  reports: {
    all: ['tms', 'reports'] as const,
    lists: () => [...queryKeys.reports.all, 'list'] as const,
    list: (filters?: ListFilters) => [...queryKeys.reports.lists(), filters] as const,
    details: () => [...queryKeys.reports.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.reports.details(), id] as const,
    stats: () => [...queryKeys.reports.all, 'stats'] as const,
    templates: () => [...queryKeys.reports.all, 'templates'] as const,
    template: (id: string) => [...queryKeys.reports.all, 'templates', id] as const,
  },
  // Schedule query keys
  schedules: {
    all: ['tms', 'schedules'] as const,
    lists: () => [...queryKeys.schedules.all, 'list'] as const,
    list: (filters?: ListFilters) => [...queryKeys.schedules.lists(), filters] as const,
    details: () => [...queryKeys.schedules.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.schedules.details(), id] as const,
    stats: () => [...queryKeys.schedules.all, 'stats'] as const,
    upcoming: (limit?: number) => [...queryKeys.schedules.all, 'upcoming', limit] as const,
    forReport: (reportId: string) => [...queryKeys.schedules.all, 'report', reportId] as const,
  },
  // Schema metadata query keys (for Report Designer)
  schema: {
    all: ['tms', 'schema'] as const,
    tables: (locale?: string, module?: string) =>
      [...queryKeys.schema.all, 'tables', locale, module] as const,
    table: (tableName: string, locale?: string) =>
      [...queryKeys.schema.all, 'table', tableName, locale] as const,
    modules: () => [...queryKeys.schema.all, 'modules'] as const,
  },
  // AI Routing query keys (Phase 6)
  aiRouting: {
    all: ['tms', 'aiRouting'] as const,
    // Trip-level operations
    tripOptimization: (tripId: string) =>
      [...queryKeys.aiRouting.all, 'trip', tripId, 'optimization'] as const,
    tripStatus: (tripId: string) => [...queryKeys.aiRouting.all, 'trip', tripId, 'status'] as const,
    tripRecommendations: (tripId: string) =>
      [...queryKeys.aiRouting.all, 'trip', tripId, 'recommendations'] as const,
    // Route-level operations
    routeFactors: (routeId: string) =>
      [...queryKeys.aiRouting.all, 'route', routeId, 'factors'] as const,
    shouldReoptimize: (routeId: string) =>
      [...queryKeys.aiRouting.all, 'route', routeId, 'shouldReoptimize'] as const,
    // Cold start
    coldStartStatus: () => [...queryKeys.aiRouting.all, 'coldStart'] as const,
    // Analytics
    patternAnalytics: (params?: { routeIds?: string[]; dateFrom?: string; dateTo?: string }) =>
      [...queryKeys.aiRouting.all, 'analytics', 'patterns', params] as const,
    weatherImpact: (params?: { routeIds?: string[]; dateFrom?: string; dateTo?: string }) =>
      [...queryKeys.aiRouting.all, 'analytics', 'weather', params] as const,
  },
};

export default queryClient;
